import React from 'react';
import { Menu } from 'antd';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import { MonitoredFlow } from 'app/model/client/CORE';
import { MonitoredLoadValue, MonitoredOccupancyLoadValues, TimePeriod } from 'app/model/CORE';
import memoizeOne from 'memoize-one';
import {
    AddToCustomLayerEvent,
    NewCustomLayerEvent,
    RemoveFromCustomLayerEvent,
    RemoveFromWorkspaceLayerEvent,
    ShowModalEvent,
} from 'app/common/eventbus/Events';
import {
    CustomLayerUtils,
    MapLayer,
    MapLayerUtils,
    MapObjectType,
    SolutionTvImpact,
} from 'app/components/dashboard/network/map/Map';
import { MapSelectionValues, NewLayerPopup } from 'app/components/dashboard/network/map/MapSelectionPopup';
import AppContext from 'app/common/context/AppContext';
import { Flex } from 'app/components/common/Flex';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import Utils from 'app/common/util/Utils';
import { ObjectTypeContextMenu } from 'app/components/dashboard/network/flows/menu/MapContextMenu';
import { ExtendedMonitoredTv } from 'app/model/SOL49';
import { RootState } from '../../../../../redux/store';
import {
    addToFlowSection,
    createFlowSection,
    selectUserCustomFlows,
} from '../../../../../redux/features/dashboard/user-monitored-flows-slice';
import { connect, ConnectedProps } from 'react-redux';

const mapStateToProps = (state: RootState) => {
    return {
        customFlowSections: selectUserCustomFlows(state),
    };
};
const mapMapDispatchToProps = {
    createFlowSection,
    addToFlowSection,
};

const connector = connect(mapStateToProps, mapMapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface MapTVImpactContextMenuProps extends ObjectTypeContextMenu, PropsFromRedux {
    tvImpacts: Array<SolutionTvImpact>;
    workspaceTvIds: Array<string>;
    tvSetMap: { [key: string]: string };
    onClose(): void;
}

const ACTION_SHOW_DETAILS = 'show-details';
const ACTION_CREATE_NEW_REGULATION = 'create-new-regulation';
const ACTION_CREATE_NEW_SCENARIO = 'create-new-scenario';
const ACTION_FIND_SOLUTION = 'find-solution';
const ACTION_CREATE_CUSTOM_SECTION = 'create-custom-section';
const ACTION_ADD_TO_SECTION = 'add-to-custom-section';
const ACTION_CREATE_CUSTOM_LAYER = 'create-custom-layer';
const ACTIONS = [
    ACTION_SHOW_DETAILS,
    ACTION_CREATE_NEW_REGULATION,
    ACTION_CREATE_NEW_SCENARIO,
    ACTION_FIND_SOLUTION,
    ACTION_CREATE_CUSTOM_SECTION,
    ACTION_ADD_TO_SECTION,
    ACTION_CREATE_CUSTOM_LAYER,
];

export class MapTVImpactContextMenu extends React.PureComponent<MapTVImpactContextMenuProps> {
    render() {
        return (
            <Menu
                key="map-tv-impact-context-menu"
                items={this.getMenuItems()}
                onClick={this.handleOnClick}
                triggerSubMenuAction="click"
            />
        );
    }

    getMenuItems = () => {
        const subMenu = this.props.tvImpacts?.map(tvImpact => ({
            label: this.getTvNameLabel(tvImpact),
            key: `${tvImpact.tvId}-${tvImpact.type}`,
            className: 'menuItemConvection gapInMargin',
            children: [
                {
                    label: tvImpact.tvId,
                    key: `tvImpactLoad-${tvImpact.tvId}-${tvImpact.type}`,
                    disabled: true, //otherwise when clicking it hides the menu
                },
                { key: 'divider-1', type: 'divider' },
                {
                    label: 'Show details (graph)',
                    key: this.actionKey(ACTION_SHOW_DETAILS, tvImpact.tvId, tvImpact.type),
                },
                { key: 'divider-2', type: 'divider' },
                {
                    label: 'Create a new regulation',
                    key: this.actionKey(ACTION_CREATE_NEW_REGULATION, tvImpact.tvId, tvImpact.type),
                },
                {
                    label: 'Find solution',
                    key: this.actionKey(ACTION_FIND_SOLUTION, tvImpact.tvId, tvImpact.type),
                },
                { key: 'divider-3', type: 'divider' },
                {
                    label: 'Create a custom section',
                    key: this.actionKey(ACTION_CREATE_CUSTOM_SECTION, tvImpact.tvId, tvImpact.type),
                },
                {
                    label: 'Add to custom section',
                    key: this.actionKey(ACTION_ADD_TO_SECTION, tvImpact.tvId, tvImpact.type),
                    children: [
                        ...this.props.customFlowSections?.map(flow => ({
                            key: 'add-to-section-' + flow.uuid + tvImpact.tvId + tvImpact.type,
                            onClick: () => this.addToCustomSectionMenuHandleUpdateSection(tvImpact.tvId, flow),
                            label: `Add to: ${flow.flowName}`,
                        })),
                    ],
                },
                { key: 'divider-4', type: 'divider' },
                {
                    key: this.actionKey(ACTION_CREATE_CUSTOM_LAYER, tvImpact.tvId, tvImpact.type),
                    label: 'Create a custom layer',
                },
                this.addToCustomLayer(tvImpact.tvId, tvImpact.type),
                ...this.removeFromCustomLayerMenu(tvImpact.tvId, tvImpact.type),
                this.removeFromWorkspaceLayer(tvImpact.tvId, tvImpact.type),
            ],
        }));
        if (this.props.group) {
            return [
                {
                    label: <span className="detailed-subpanel-title">TFV ({this.props.tvImpacts.length})</span>,
                    key: 'tfvs',
                    children: subMenu,
                },
            ];
        } else {
            return subMenu;
        }
    };

    private getTvNameLabel(tvImpact: SolutionTvImpact) {
        const monitoredTv = tvImpact.monitoredTv;
        const monitoredTvBefore = tvImpact.monitoredTvBefore;
        const timePeriod = tvImpact.timePeriod;
        return (
            <div style={{ width: '21em' }}>
                <Flex grow={false}>
                    <Flex size="6.5em">
                        TV:{' '}
                        <span title={tvImpact.type} style={{ color: 'white' }}>
                            {monitoredTv.refLocation.id}
                        </span>
                    </Flex>
                    <Flex size="14em" direction="vertical" style={{ alignSelf: 'center', paddingLeft: '1em' }}>
                        {monitoredTvBefore?.loadValues && (
                            <EntryLoadBar
                                title={'Before'}
                                key="after-entry-load"
                                labelTitle={'Entry Before'}
                                loadValues={monitoredTvBefore.loadValues}
                                monitoredPeriod={timePeriod}
                                activePeriods={monitoredTvBefore.activePeriods}
                                currentTime={AppContext.getCurrentTime()}
                                drawBottomLine={true}
                                showLoadInactivePeriods={true}
                                barHeightEm={0.5}
                            />
                        )}
                        {monitoredTv?.loadValues && (
                            <EntryLoadBar
                                title={'After'}
                                key="entry-load"
                                label={'+'}
                                labelTitle={'Entry After'}
                                loadValues={monitoredTv.loadValues}
                                monitoredPeriod={timePeriod}
                                activePeriods={monitoredTv.activePeriods}
                                currentTime={AppContext.getCurrentTime()}
                                drawBottomLine={true}
                                showLoadInactivePeriods={true}
                                barHeightEm={0.5}
                            />
                        )}
                        {this.renderOccLoadValuesGroupByDuration(monitoredTv, monitoredTvBefore, timePeriod)}
                    </Flex>
                </Flex>
            </div>
        );
    }

    renderOccLoadValuesGroupByDuration(
        monitoredTv: ExtendedMonitoredTv,
        monitoredTvBefore: ExtendedMonitoredTv,
        timePeriod: TimePeriod,
    ) {
        let occupancyLoadValues = new Map<number, MonitoredOccupancyLoadValues>();
        let occupancyLoadValuesBefore = new Map<number, MonitoredOccupancyLoadValues>();
        if (monitoredTv.occupancyLoadValues) {
            occupancyLoadValues = monitoredTv.occupancyLoadValues.reduce((map, olv) => {
                map.set(olv.durationInMin, olv);
                return map;
            }, new Map<number, MonitoredOccupancyLoadValues>());
        }
        if (monitoredTvBefore.occupancyLoadValues) {
            occupancyLoadValuesBefore = monitoredTvBefore.occupancyLoadValues.reduce((map, olv) => {
                map.set(olv.durationInMin, olv);
                return map;
            }, new Map<number, MonitoredOccupancyLoadValues>());
        }
        console.log('occupancyLoadValues', occupancyLoadValues);
        if (Array.from(occupancyLoadValues.keys())?.length > 0) {
            const output = [];
            Array.from(occupancyLoadValues.keys()).map(durationInMin => {
                const occupancyValues = occupancyLoadValues.get(durationInMin);
                const occupancyValuesBefore = occupancyLoadValuesBefore.get(durationInMin);
                if (occupancyValuesBefore) {
                    output.push(
                        <OccupancyLoadBar
                            title={'Before (' + durationInMin + ')'}
                            key={'after-occupancy-load-' + occupancyValues.durationInMin}
                            labelTitle={'Occupancy Before'}
                            loadValues={occupancyValues.occupancyLoadValues}
                            monitoredPeriod={timePeriod}
                            activePeriods={monitoredTvBefore.activePeriods}
                            currentTime={AppContext.getCurrentTime()}
                            drawBottomLine={true}
                            showLoadInactivePeriods={true}
                            barHeightEm={0.5}
                        />,
                    );
                }
                if (occupancyValues) {
                    output.push(
                        <OccupancyLoadBar
                            title={'After(' + durationInMin + ')'}
                            label={'+'}
                            labelTitle={'Occupancy After'}
                            key={'occupancy-load-' + occupancyValues.durationInMin}
                            loadValues={occupancyValues.occupancyLoadValues}
                            monitoredPeriod={timePeriod}
                            activePeriods={monitoredTv.activePeriods}
                            currentTime={AppContext.getCurrentTime()}
                            drawBottomLine={true}
                            showLoadInactivePeriods={true}
                            barHeightEm={0.5}
                        />,
                    );
                }
            });
            return output;
        }
        return undefined;
    }

    /**
     * Extract monitored period from load values
     */
    getMonitoredPeriod = memoizeOne((loadValues: Array<MonitoredLoadValue>) => {
        return MonitoredLoadUtils.getMonitoredPeriod(loadValues);
    });

    removeFromCustomLayerMenu(tvName: string, extra?: string) {
        const tvCustomLayers = this.getTvCustomLayers(tvName);
        if (tvCustomLayers?.length > 0) {
            return [
                { key: 'divider-' + tvName + extra, type: 'divider' },
                {
                    key: 'remove-from-layer-' + tvName + extra,
                    label: 'Remove from custom layer',
                    children: [
                        ...tvCustomLayers.map(customLayer => ({
                            key: 'remove-from-layer-' + customLayer.id + tvName + extra,
                            label: `Remove from: ${customLayer.label}`,
                            onClick: () => this.removeFromCustomLayerMenuHandleUpdateSection(tvName, customLayer),
                        })),
                    ],
                },
            ];
        }
        return [];
    }

    addToCustomLayer(tvName: string, extra?: string) {
        const result = {
            label: 'Add to custom layer',
            key: 'add-to-custom-layer-menu-' + tvName + extra,
            children: [
                ...CustomLayerUtils.customLayers
                    .filter(customLayer => !CustomLayerUtils.containsTv(customLayer, tvName))
                    .map(customLayer => ({
                        key: 'add-to-layer-' + customLayer.id + tvName + extra,
                        label: `Add to: ${customLayer.label}`,
                        onClick: () => this.addToCustomLayerMenuHandleUpdateSection(tvName, customLayer),
                    })),
            ],
        };

        if (result.children.length > 0) {
            return result;
        }
    }

    removeFromWorkspaceLayer(tvName: string, extra?: string) {
        if (Utils.contains(this.props.workspaceTvIds, tvName)) {
            return {
                key: 'remove-from-workspace-layer-' + tvName + extra,
                label: 'Remove from workspace layer',
                onClick: () => this.removeFromWorkspaceLayerMenuHandle(tvName),
            };
        }
    }

    getTvCustomLayers(tvName: string) {
        return CustomLayerUtils.customLayers.filter(
            customLayer =>
                customLayer.objectsToDisplay.filter(
                    objectToDisplay =>
                        MapObjectType.TV === objectToDisplay.type &&
                        tvName === MapLayerUtils.EXTENDED_MONITORED_TV_TV_ID_PROVIDER.getKey(objectToDisplay.object),
                )?.length > 0,
        );
    }

    addToCustomSectionMenuHandleUpdateSection(tvName: string, flow: MonitoredFlow) {
        //close the context menu first
        this.props.onClose();
        this.props.addToFlowSection({ flow: flow, tvsToAdd: [tvName] });
    }

    addToCustomLayerMenuHandleUpdateSection(tvName: string, customLayer: MapLayer) {
        this.props.onClose();
        AddToCustomLayerEvent.fire(customLayer, tvName, MapObjectType.TV);
    }

    removeFromCustomLayerMenuHandleUpdateSection(tvName: string, customLayer: MapLayer) {
        this.props.onClose();
        RemoveFromCustomLayerEvent.fire(customLayer, tvName, MapObjectType.TV);
    }

    removeFromWorkspaceLayerMenuHandle(tvName: string) {
        this.props.onClose();
        RemoveFromWorkspaceLayerEvent.fireRemoveTv(tvName);
    }

    actionKey(action: string, tvName: string, extra?: string) {
        if (extra) {
            return `${action}_${tvName}_${extra}`;
        } else {
            return `${action}_${tvName}`;
        }
    }

    handleOnClick = event => {
        const parsedKey = event.key.split('_');
        const action = parsedKey[0].trim();
        //check if is a valid action
        if (!ACTIONS.includes(action)) return;

        const tvName = parsedKey[1];
        const solutionTvImpact = this.props.tvImpacts.find(tvImpact => tvImpact.tvId === tvName);
        if (!solutionTvImpact) return;

        //close the context menu first
        this.props.onClose();

        switch (action) {
            case ACTION_SHOW_DETAILS:
                ModalHandler.showTvImpactDetails(tvName, this.props.tvSetMap[tvName], solutionTvImpact);
                break;
            case ACTION_CREATE_NEW_REGULATION:
                ModalHandler.showRegulationEditor(tvName, 'tv');
                break;
            case ACTION_CREATE_NEW_SCENARIO:
                //TODO
                break;
            case ACTION_FIND_SOLUTION:
                ModalHandler.showFindSolution([{ tvId: tvName }]);
                break;
            case ACTION_CREATE_CUSTOM_SECTION:
                this.props.createFlowSection({ tvIds: [tvName] });
                break;
            case ACTION_CREATE_CUSTOM_LAYER:
                this.showCustomLayerNamePopup(tvName);
                break;
        }
    };

    showCustomLayerNamePopup(tvName: string) {
        const value: MapSelectionValues = Object.assign(
            {},
            {
                [MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]: {
                    label: 'TFV',
                    options: [tvName],
                    selected: [tvName],
                },
            },
        );
        const content = <NewLayerPopup onSave={name => this.handleSaveNewLayer(value, name)} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '200px'));
    }

    handleSaveNewLayer = (value: MapSelectionValues, name: string) => {
        const values: Map<MapObjectType, Array<string>> = new Map<MapObjectType, Array<string>>();
        values.set(MapObjectType.TV, value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].selected);
        NewCustomLayerEvent.fire(name, values);
    };
}

export default connector(MapTVImpactContextMenu);
